import { zip } from 'fflate';

import { downloadBlob } from '@/utils/csvDownload';

interface ZipFile {
  name: string;
  url: string;
}

// Bundle remote files into a single .zip and download it.
//
// NOTE: each file's bytes must be readable, so the host has to allow CORS for
// this origin — an opaque (no-cors) response yields an empty archive entry.
export async function downloadFilesAsZip(files: ZipFile[], zipName: string): Promise<void> {
  // Fetch in parallel; a single failure rejects the whole batch so we never
  // hand the user a half-complete archive.
  const fetched = await Promise.all(
    files.map(async ({ name, url }) => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch ${name} (${res.status})`);
      return { bytes: new Uint8Array(await res.arrayBuffer()), name };
    }),
  );

  // A zip is a flat name→bytes map, so duplicate filenames would overwrite each
  // other. Disambiguate deterministically: "file.pdf", "file (1).pdf", ...
  const entries: Record<string, Uint8Array> = {};
  const usedNames = new Set<string>();
  for (const { bytes, name } of fetched) {
    entries[dedupeName(name, usedNames)] = bytes;
  }

  const archive = await new Promise<Uint8Array>((resolve, reject) => {
    zip(entries, (err, data) => (err ? reject(err) : resolve(data)));
  });

  downloadBlob(archive, zipName, 'application/zip');
}

function dedupeName(name: string, used: Set<string>): string {
  const reserve = (candidate: string) => {
    used.add(candidate);
    return candidate;
  };

  if (!used.has(name)) return reserve(name);

  const dot = name.lastIndexOf('.');
  const base = dot === -1 ? name : name.slice(0, dot);
  const ext = dot === -1 ? '' : name.slice(dot);

  let i = 1;
  while (used.has(`${base} (${i})${ext}`)) i += 1;
  return reserve(`${base} (${i})${ext}`);
}
